import { useState, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@loader/ui';

import { formatScheduleDisplay } from './schedulePickerUtils';

import type { SyncProgressInfo } from '../hooks/useSyncTrigger';

export { ConnectorSyncRow } from './ConnectorSyncRow';
export type { ConnectorSyncRowProps } from './ConnectorSyncRow';

// ============================================================================
// Normalised status (used by both Nango + custom connector rows)
// ============================================================================

export type SyncStatus = 'syncing' | 'active' | 'paused' | 'error' | 'inactive';

/**
 * Resolve the effective sync status by reconciling optimistic UI state
 * with the backend progress poll. Backend is source of truth:
 * - If backend says syncing → 'syncing'
 * - If UI says syncing but backend doesn't → 'active' (sync finished)
 * - Otherwise → pass-through
 */
export function resolveEffectiveStatus(status: SyncStatus, backendSyncing: boolean): SyncStatus {
  if (backendSyncing) return 'syncing';
  if (status === 'syncing') return 'active';
  return status;
}

/**
 * Derive a normalised SyncStatus from connector state.
 *
 * Works for all connector types (Nango, native, custom). Pass the
 * provider-specific status string for Nango, or omit it for connectors
 * that derive state from their own fields.
 */
export function toSyncStatus(opts: {
  enabled: boolean;
  isSyncing?: boolean;
  lastSyncedAt?: string | null;
  /** Nango sync status string (RUNNING/SUCCESS/PAUSED/ERROR) */
  providerStatus?: string;
}): SyncStatus {
  if (opts.isSyncing) return 'syncing';
  if (opts.providerStatus) {
    switch (opts.providerStatus) {
      case 'RUNNING': return 'syncing';
      case 'SUCCESS': return 'active';
      case 'PAUSED': return 'paused';
      case 'ERROR': return 'error';
      default: return 'inactive';
    }
  }
  if (!opts.enabled) return 'paused';
  if (!opts.lastSyncedAt) return 'syncing';
  return 'active';
}

// ============================================================================
// Status dot
// ============================================================================

const DOT_COLORS: Record<SyncStatus, string> = {
  syncing: '', // uses spinner
  active: 'bg-green-500',
  paused: 'bg-yellow-500',
  error: 'bg-red-500',
  inactive: 'bg-muted-foreground/40',
};

export function SyncStatusDot({ status }: { status: SyncStatus }) {
  if (status === 'syncing') {
    return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-blue-500" />;
  }
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_COLORS[status]}`} />;
}

// ============================================================================
// Status badge
// ============================================================================

const BADGE_CONFIG: Record<SyncStatus, { label: string; variant: 'info' | 'success' | 'warning' | 'destructive' | 'muted' }> = {
  syncing: { label: 'Syncing', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  paused: { label: 'Paused', variant: 'warning' },
  error: { label: 'Error', variant: 'destructive' },
  inactive: { label: 'Inactive', variant: 'muted' },
};

export function SyncStatusBadge({ status, progress }: { status: SyncStatus; progress?: SyncProgressInfo | null }) {
  const { label, variant } = BADGE_CONFIG[status];

  if (status === 'syncing') {
    let progressText: string | null = null;
    if (progress) {
      switch (progress.stage) {
        case 'connecting':
          progressText = 'Connecting...';
          break;
        case 'fetching':
          if (progress.recordsFetched > 0) {
            if (progress.totalRecords && progress.totalRecords > 0) {
              const pct = Math.min(Math.round((progress.recordsFetched / progress.totalRecords) * 100), 99);
              progressText = `${pct}%`;
            } else {
              progressText = `${progress.recordsFetched.toLocaleString()} rows`;
            }
          } else {
            progressText = 'Downloading...';
          }
          break;
        case 'processing':
          progressText = 'Creating version...';
          break;
      }
    }

    return (
      <Badge variant={variant} className="gap-1">
        {label}
        {progressText && (
          <span className="tabular-nums font-normal opacity-80">· {progressText}</span>
        )}
      </Badge>
    );
  }

  return <Badge variant={variant}>{label}</Badge>;
}

// ============================================================================
// Sync Now menu item
// ============================================================================

export function SyncNowMenuItem({
  isSyncing,
  disabled,
  onTrigger,
}: {
  isSyncing: boolean;
  disabled?: boolean;
  onTrigger: () => void;
}) {
  return (
    <DropdownMenuItem onClick={onTrigger} disabled={disabled || isSyncing}>
      <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
      {isSyncing ? 'Syncing...' : 'Sync Now'}
    </DropdownMenuItem>
  );
}

// ============================================================================
// Schedule info (clock icon + frequency text)
// ============================================================================

export function ScheduleInfo({ schedule }: { schedule: string | null | undefined }) {
  if (!schedule) return null;
  return (
    <span className="text-muted-foreground flex items-center gap-1 text-xs">
      <Clock className="h-3 w-3" />
      {formatScheduleDisplay(schedule)}
    </span>
  );
}

// ============================================================================
// Schedule menu item (reusable dropdown item for opening the schedule dialog)
// ============================================================================

export function ScheduleMenuItem({
  schedule,
  onClick,
}: {
  schedule: string | null | undefined;
  onClick: () => void;
}) {
  return (
    <DropdownMenuItem onClick={onClick}>
      <Clock className="mr-2 h-3.5 w-3.5" />
      Schedule
      {schedule && (
        <span className="text-muted-foreground ml-auto pl-2 text-xs">
          {formatScheduleDisplay(schedule)}
        </span>
      )}
    </DropdownMenuItem>
  );
}

// ============================================================================
// Integration card (collapsible group shell used by all connector groups)
// ============================================================================

export interface IntegrationCardProps {
  /** Icon element (image or lucide icon) */
  icon: ReactNode;
  /** Group title */
  label: string;
  /** Counter text shown after the label (e.g. "3 tables", "2/5 active") */
  subtitle: string;
  /** Optional header-level dropdown menu items (e.g. "Sync All", "Disconnect") */
  headerMenuItems?: ReactNode;
  children: ReactNode;
}

export function IntegrationCard({
  icon,
  label,
  subtitle,
  headerMenuItems,
  children,
}: IntegrationCardProps) {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-border overflow-hidden rounded-lg border">
        <CollapsibleTrigger asChild>
          <div className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 px-4 py-2.5">
            {open ? (
              <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            )}
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              {icon}
              <span className="font-semibold">{label}</span>
              <span className="text-muted-foreground text-sm">{subtitle}</span>
            </div>
            {headerMenuItems && (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {headerMenuItems}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-border border-t">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

